import {
  FieldErrorsImpl,
  UseFormRegister,
  UseFormResetField,
  UseFormWatch,
  UseFormSetValue,
  UseFormReset
} from 'react-hook-form';
import {
  CalculateOverdueKind,
  EMIBreakUpSubType,
  enachMode,
  EnachSetupKinds
} from '../constants/EmiSchedule.constant';
import {
  CustomerDetailApiResponse,
  Due,
  FeeDetailsEntity,
  IPendingFeeComponents,
  PartPrePayment
} from './CaseDetail.type';
import { Repayment } from './Overview.type';
export interface IsetupEnach {
  mode: enachMode;
  number: string;
  open: boolean;
  loading: boolean;
  sentLink: boolean;
}

export interface EnachSetupAction {
  type: EnachSetupKinds;
}

export interface MonthlyEnach {
  amount?: string | number;
  enachRequestRaisedOn?: string;
  enachRequestStatus?: string;
}
export interface IDisplayItems {
  header: string;
  color: 'black' | 'red';
  value: ((data: CustomerDetailApiResponse) => string | number) | string;
  monthlyEnach?: Array<MonthlyEnach>;
  boldPart?: string;
  checkPluralization?: boolean;
}

export interface EMIBreakUp {
  referenceId: string;
  type: string;
  subType: keyof typeof EMIBreakUpSubType;
  amount: number;
  amountPaid: number;
  gst: number;
  gstPaid: number;
  waiverAmount: number;
  waiverGst: number;
  heldAmount: number;
  heldGst: number;
  unpaidDueAmount: number;
  totalDueAmount: number;
  deferredAmount: number;
  deferredGst: number;
  residualAdjustment: number;
  lastAppliedDate: string;
  startDate?: string;
  endDate?: string;
}
interface ResponseKeys {
  value: number;
  currency: string;
}

export interface OverdueResponse {
  interest: ResponseKeys;
  principal: ResponseKeys;
  lateFee: ResponseKeys;
  bounceFee: ResponseKeys;
  penalInterest: ResponseKeys;
}

export interface CalculateOverdueAction {
  type: CalculateOverdueKind;
  payload?: {
    date?: string;
    overdueData?: Array<JSX.Element>;
    overdueOnData?: JSX.Element;
  };
}

export interface ICalculateOverdue {
  open: boolean;
  loading: boolean;
  date: string;
  expand: boolean;
  overdueData: Array<JSX.Element> | null;
  overdueOn: JSX.Element | null;
  overdueAmount?: number;
}

export interface PendingFeeComponent {
  dueReferenceId: string;
  dueComponentReferenceId: string;
  dueDate: string;
  feeType: keyof typeof WaiveFeeType;
  originalFeeAmount: number;
  originalFeeGst: number;
  revisedFeeAmount: number;
  revisedFeeGst: number;
  waivedAmount: number;
  waivedGst: number;
  deferred: boolean;
  amountPending: number;
  gstPending: number;
  deferredAmount: number;
  deferredGst: number;
  heldAmount: number;
  heldGst: number;
  heldTill: string;
  availableActions: Array<Option>;
  flowType?: EmiFlowType;
}

export enum WaiveFeeType {
  LATE_FEE = 'Late fee',
  BOUNCE_FEE = 'Bounce fee',
  OVERDUE_INTEREST = 'Overdue interest',
  PENALTY_CHARGES = 'EMI penalty charges'
}

export enum EmiFlowType {
  OLD_FLOW = 'OLD_FLOW',
  NEW_FLOW = 'NEW_FLOW'
}

export interface Option {
  label: string;
  value: any;
  disabled?: boolean;
}

export interface Amount {
  value: number;
  currency: string;
}
export interface WaiveFeeFormFields {
  componentAdjustments: { [key: string]: ComponentAdjustment };
}

export interface WaiveFeeFormFieldsV2 {
  componentAdjustments: { [key: string]: ComponentAdjustmentV2 };
}

export interface ComponentAdjustmentV2 {
  waiverAmount: number;
}

export interface ComponentAdjustment extends PendingFeeComponent {
  amount: Amount;
  waiverAmount: Amount;
  dueComponentReferenceId: string;
  dueReferenceId: string;
  remark: string;
  requestType: string;
  duration: string;
  isFormOpen: boolean;
}
export interface WaiveFeeDrawerProps {
  pendingFeeComponents: Array<PendingFeeComponent>;
  isOpen: boolean;
  toggleDrawer: (val: boolean) => void;
  dueDetails: Due | undefined;
  phoneNumber: string | undefined;
  phoneNumberCustomId?: string;
  flowType: Record<string, EmiFlowType>;
}

export interface WaiveFeeFormProps {
  pendingFeeComponent: FeeDetailsEntity;
  register: UseFormRegister<WaiveFeeFormFields>;
  errors: Partial<FieldErrorsImpl<WaiveFeeFormFields>>;
  watch: UseFormWatch<WaiveFeeFormFields>;
  index: string;
  resetField: UseFormResetField<WaiveFeeFormFields>;
  calculateTotalWaiverAmount: any;
  setValue: UseFormSetValue<WaiveFeeFormFields>;
  flowType: EmiFlowType;
  reset: UseFormReset<WaiveFeeFormFields>;
  dueDetails: Due | undefined;
}

export interface WaiveFeePayload {
  amount: Amount;
  waiverAmount: Amount;
  dueComponentReferenceId: string;
  dueReferenceId: string;
  remark: string;
  requestType: string;
}

export interface EmiCalendarActionCellProps {
  toggleWaiveFeeDrawer: (val: boolean) => void;
  toggleWaiveFeeDrawerV2: (val: boolean) => void;
  setFeeComponentAdjustmentsForDue: (val: PendingFeeComponent[]) => void;
  pendingFeeComponents: IPendingFeeComponents;
  rowData: Due | PartPrePayment;
  togglePaymentHistoryOverlay: () => void;
  handleTableRowData: (params: any) => void;
  dispatchDueRepayment: React.Dispatch<DueRepaymentAction>;
  handleReferenceId: (param: string, type: string) => void;
  setDueDetails: (val: Due) => void;
  setFlowType: React.Dispatch<React.SetStateAction<Record<string, EmiFlowType>>>;
  editAccessFlag?: boolean;
  isExpanded?: boolean;
}
export interface IDueRepayment {
  open: boolean;
  loading: boolean;
  repaymentDueData: Repayment[] | null;
  dueReferenceId: string | null;
}

export interface DueRepaymentAction {
  type: DueRepaymentKind;
  payload?: {
    data?: Repayment[];
    dueReferenceId?: string;
  };
}

export enum DueRepaymentKind {
  INITIAL_STATE,
  CLOSE,
  BEFORE_API_CALL,
  API_ERROR,
  API_CALL_SUCCESS,
  GET_DUE_REFERENCE_ID
}

export interface IAmount {
  amount: number;
  currency: string;
}

export interface SendPaymentLinkRequest {
  loanAccountNumber: string;
  notifyToAlternateContact: boolean;
  alternateContactNumber: string;
  customAmount: IAmount;
  customAmountProvided: boolean;
  templateName: string;
  placeholders: Record<string, string>;
}

export interface IFeeWaiveSubmitPayload {
  dueReferenceId: string;
  waiverAmount: number;
}
